'use client';

import React from 'react';
import styled from 'styled-components';
import { DensityProvider } from '@liftango/ui-tokens';
import { FilterTag } from '../../primitives/FilterTag/FilterTag';
import { Button } from '../../primitives/Button/Button';

/**
 * One row in the active-filters strip.
 *
 * - `id`: stable key (e.g. the filter dimension name).
 * - `label`: the dimension and value joined for display, like
 *   `'Depot: North'` or `'Status: On-time, Late'`.
 * - `onDismiss`: clears just this dimension; the rest stay in place.
 */
export interface FilterBarActiveFilter {
  id: string;
  label: React.ReactNode;
  onDismiss: () => void;
}

export interface FilterBarProps {
  /** The controls row content — usually `FilterBar.Search`, `FilterBar.Group`, `FilterBar.Actions`. */
  children: React.ReactNode;
  /**
   * Currently-applied filters rendered as a second row of dismissible tags.
   * Pass an empty array (or omit) to suppress the row entirely.
   */
  activeFilters?: FilterBarActiveFilter[];
  /**
   * Called when the user clicks the "Clear all" affordance. Only rendered
   * when two or more filters are active; for a single filter the per-tag
   * dismiss button is enough.
   */
  onClearAll?: () => void;
  className?: string;
}

const Surface = styled.div`
  display: grid;
  gap: ${(p) => p.theme.spaceAlias.sm};
  padding: ${(p) => p.theme.spaceAlias.sm} ${(p) => p.theme.spaceAlias.md};
  background: ${(p) => p.theme.colors.neutral.panel};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.lg};
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(p) => p.theme.spaceAlias.sm};
`;

const ActiveRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(p) => p.theme.spaceAlias.xs};
  padding-top: ${(p) => p.theme.spaceAlias.sm};
  border-top: 1px solid ${(p) => p.theme.colors.neutral.line};
`;

/**
 * Left-anchored search slot. Wrap a `SearchInput` to give it the
 * filter bar's preferred minimum width without writing inline styles
 * at every callsite.
 */
const Search = styled.div`
  display: flex;
  align-items: center;
  min-width: 240px;
  max-width: 360px;
  flex: 1 1 240px;

  & > * {
    width: 100%;
  }
`;

/**
 * A horizontal group of filter selectors (`SegmentedControl`, `Select`,
 * `Popover`-anchored buttons, …). Use one or several. Heights are forced
 * to 28 px for everything inside the bar via the filter density context.
 */
const Group = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(p) => p.theme.spaceAlias.xs};
`;

/**
 * Right-aligned action group, separated from the selectors by a hairline
 * divider. Use for `Reset` and saved-view affordances. Subordinate to the
 * filters themselves; pass `kind="ghost"` Buttons inside.
 */
const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.xs};
  margin-left: auto;
  padding-left: ${(p) => p.theme.spaceAlias.sm};
  border-left: 1px solid ${(p) => p.theme.colors.neutral.line};
`;

function FilterBarRoot({ children, activeFilters, onClearAll, className }: FilterBarProps) {
  const showClearAll = (activeFilters?.length ?? 0) >= 2 && onClearAll !== undefined;
  return (
    <DensityProvider density="filter">
      <Surface className={className}>
        <ControlsRow role="toolbar" aria-label="Filters">
          {children}
        </ControlsRow>
        {activeFilters && activeFilters.length > 0 && (
          <ActiveRow aria-label="Active filters">
            {activeFilters.map((filter) => (
              <FilterTag key={filter.id} onClear={filter.onDismiss}>
                {filter.label}
              </FilterTag>
            ))}
            {showClearAll && (
              <Button kind="ghost" size="sm" onClick={onClearAll}>
                Clear all
              </Button>
            )}
          </ActiveRow>
        )}
      </Surface>
    </DensityProvider>
  );
}

/**
 * Filter bar composite for high-volume operator views (booking lists,
 * vehicle queues, schedule grids).
 *
 * Forces uniform 28 px control heights, owns the bar chrome (1 px outline,
 * card-radius, 8/12 px inset), and renders an optional active-filters row
 * with center-aligned `FilterTag`s plus a "Clear all" ghost button when
 * two or more filters are active.
 *
 * Use the slotted children to keep layout consistent across consumers:
 *
 * ```tsx
 * <FilterBar
 *   activeFilters={active}
 *   onClearAll={resetAll}
 * >
 *   <FilterBar.Search>
 *     <SearchInput …/>
 *   </FilterBar.Search>
 *   <FilterBar.Group>
 *     <SegmentedControl …/>
 *     <Select …/>
 *   </FilterBar.Group>
 *   <FilterBar.Actions>
 *     <Button kind="ghost">Reset</Button>
 *   </FilterBar.Actions>
 * </FilterBar>
 * ```
 */
export const FilterBar = Object.assign(FilterBarRoot, {
  Search,
  Group,
  Actions: ActionsGroup,
});
